/**
 * MarkdownHelpSection
 *
 * Renders help / how-to content as scannable, structured steps
 * (heading + optional description + numbered steps, each with a short
 * title and body) instead of dense hardcoded prose.
 *
 * All copy is i18n-driven via `useAppTranslation` — pass translation keys
 * (under the `howto.*` namespace) and an optional English fallback for each.
 * Reusable across any help/how-to surface.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAppTranslation } from '@/contexts/TranslationContext';

export interface HelpStep {
  /** i18n key for the step's short title */
  titleKey: string;
  /** i18n key for the step's body/detail */
  bodyKey: string;
  /** Optional English fallback for the title (shown until translated) */
  titleFallback?: string;
  /** Optional English fallback for the body */
  bodyFallback?: string;
}

export interface MarkdownHelpSectionProps {
  /** i18n key for the section heading */
  titleKey: string;
  /** i18n key for the optional section description */
  descriptionKey?: string;
  /** Ordered steps to render */
  steps: HelpStep[];
  /** Optional English fallbacks for heading/description */
  titleFallback?: string;
  descriptionFallback?: string;
  /** Optional extra classes for the outer card */
  className?: string;
}

export function MarkdownHelpSection({
  titleKey,
  descriptionKey,
  steps,
  titleFallback,
  descriptionFallback,
  className,
}: MarkdownHelpSectionProps) {
  const { t } = useAppTranslation();

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">{t(titleKey, titleFallback)}</CardTitle>
        {descriptionKey && (
          <CardDescription>{t(descriptionKey, descriptionFallback)}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="space-y-3">
          {steps.map((step, index) => (
            <li key={step.titleKey} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium flex-shrink-0">
                {index + 1}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {t(step.titleKey, step.titleFallback)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(step.bodyKey, step.bodyFallback)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

export default MarkdownHelpSection;
